import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { TextareaAutosize, Button, TextField, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import isEmpty from 'lodash/isEmpty';

import { formatPrice } from 'utils/helpers';
import useModalOpen from 'hooks/useModalOpen';
import TransitionDialog from 'components/TransitionDialog';
import styles from 'styles/components/CenteredDialog.module.scss';

const useStyles = makeStyles(theme => ({
  paper: {
    height: 'fit-content',
    width: '90%',
    marginBottom: 'auto',
    top: '15%',
    borderRadius: 10,

    [theme.breakpoints.up('md')]: {
      width: '50%',
    },

    [theme.breakpoints.up('lg')]: {
      width: '30%',
    },
  },

  paperFullScreenDialog: {
    width: '100%',
  },
}));

const CenteredDialog = ({
  modalName,
  title,
  subtitle,
  textarea,
  priceField,
  defaultPrice,
  handleSubmit,
  handleDialogClose,
  placeholder,
  submitText,
  alwaysEnabled,
  isLoading,
  newBtnStyles,
  priceFieldProps,
  priceChoices,
}) => {
  const [message, setMessage] = useState('');
  const [price, setPrice] = useState(defaultPrice);
  const classes = useStyles();
  const open = useModalOpen(modalName);

  const handleClose = () => {
    handleDialogClose();
    setMessage('');
  };

  useEffect(() => setPrice(defaultPrice), [defaultPrice]);

  const formValid =
    (textarea ? message.length > 0 : true) && (price || priceChoices.length > 0 ? price > 0 : true);
  const valid = formValid && !isLoading;

  return (
    <TransitionDialog
      open={open}
      handleClose={handleClose}
      paperProps={{ className: classes.paper }}
      dialogStyles={classes.paperFullScreenDialog}
      direction="up"
      desktopDirection="up"
      noCloseIcon
    >
      <div className={styles.container}>
        <h3>{title}</h3>

        {priceField && (
          <>
            <TextField
              type="number"
              value={price}
              onChange={e => setPrice(e.target.value)}
              variant="outlined"
              fullWidth
              style={{ marginTop: 20 }}
              {...priceFieldProps}
              helperText={
                <small
                  style={{
                    color: priceFieldProps.blackHelperText ? 'black' : 'initial',
                    fontSize: 12,
                  }}
                >
                  {priceFieldProps.helperText}
                </small>
              }
            />
          </>
        )}

        {textarea && (
          <TextareaAutosize
            style={priceChoices.length > 0 ? { margin: '20px 0' } : {}}
            onChange={e => setMessage(e.target.value)}
            minRows={8}
            maxRows={8}
            placeholder={placeholder}
            autoFocus
          />
        )}

        {priceChoices.length > 0 ? (
          <>
            <h4 style={{ margin: 0 }}>Would you like to offer a discount?</h4>

            <div className={styles.priceChoices}>
              {priceChoices.map(choice => (
                <Chip
                  key={choice.value}
                  label={choice.label}
                  onClick={() => setPrice(choice.value)}
                  color={price === choice.value ? 'primary' : 'default'}
                />
              ))}
            </div>

            <div className={styles.priceContainer}>
              <div>
                You are applying for <strong className="color-main">{formatPrice(price)}</strong>
              </div>

              <small style={{ opacity: 0.8 }}>(before site fees)</small>
            </div>
          </>
        ) : null}

        {subtitle && <h4>{subtitle}</h4>}

        <div className={styles.buttons} style={!isEmpty(newBtnStyles) ? newBtnStyles : {}}>
          <Button color="primary" onClick={handleClose} fullWidth>
            Cancel
          </Button>

          <Button
            onClick={() => handleSubmit(message, price)}
            variant="contained"
            color="primary"
            disabled={!alwaysEnabled || !valid}
            fullWidth
          >
            {submitText}
          </Button>
        </div>
      </div>
    </TransitionDialog>
  );
};

CenteredDialog.propTypes = {
  modalName: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleDialogClose: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  subtitle: PropTypes.string,
  submitText: PropTypes.string,
  textarea: PropTypes.bool,
  priceField: PropTypes.bool,
  alwaysEnabled: PropTypes.bool,
  newBtnStyles: PropTypes.object,
  defaultPrice: PropTypes.number,
  priceFieldProps: PropTypes.object,
  priceChoices: PropTypes.array,
};

CenteredDialog.defaultProps = {
  placeholder: '',
  subtitle: '',
  submitText: 'Send',
  textarea: true,
  priceField: false,
  alwaysEnabled: false,
  newBtnStyles: {},
  defaultPrice: 0,
  priceFieldProps: { min: 10, max: 1000000, disabled: false, label: 'Price' },
  priceChoices: [],
};

export default CenteredDialog;
